import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography,
  Box, Paper, Grid, TextField, MenuItem, Button, FormControlLabel, Checkbox,
  useTheme, Theme, CircularProgress,
} from '@mui/material';
import LocalShipping from '@mui/icons-material/LocalShipping';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import { EquipmentDefinitionBasic, EquipmentUnit, EquipmentUnitFull } from '../types';
import { equipmentApi } from '../services/api';
import { unwrapListResponse } from '../apiUnwrapper/apiUnwrapper';
import { gradientButtonSx } from './SharedPageParts';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (equipment: EquipmentUnit) => void;
  equipment?: EquipmentUnitFull;   // populated in edit mode
  isEditMode?: boolean;
  errorMessages?: Record<string, string>;
}

const CURRENT_YEAR = new Date().getFullYear();

const EMPTY_UNIT: EquipmentUnit = {
  id: 0,
  code: '',
  displayName: '',
  isActive: true,
  deploymentYear: CURRENT_YEAR,
  manufactureYear: CURRENT_YEAR,
  definitionId: 0,
  equipmentModel: '',
};

const mapFullToUnit = (full?: EquipmentUnitFull): EquipmentUnit => {
  if (!full) return EMPTY_UNIT;
  return {
    id: full.id,
    code: full.code,
    displayName: full.displayName,
    isActive: full.isActive,
    deploymentYear: full.deploymentYear ?? CURRENT_YEAR,
    manufactureYear: full.manufactureYear ?? CURRENT_YEAR,
    definitionId: full.definitionId ?? 0,
    equipmentModel: full.equipmentModel ?? '',
  };
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AddNewEquipmentPopUp: React.FC<Props> = ({
  open,
  onClose,
  onSubmit,
  equipment: equipmentProp,
  isEditMode = false,
  errorMessages,
}) => {
  const theme: Theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [formData, setFormData] = useState<EquipmentUnit>(mapFullToUnit(equipmentProp));
  const [equipmentDefs, setEquipmentDefs] = useState<EquipmentDefinitionBasic[]>([]);
  const [defsLoading, setDefsLoading] = useState(false);

  // ── Fetch definitions when dialog opens ───────────────────────────────────

  const loadDefs = useCallback(async () => {
    try {
      setDefsLoading(true);
      const res = await equipmentApi.getEquipmentDefinitionsBasic();
      setEquipmentDefs(unwrapListResponse<EquipmentDefinitionBasic>(res));
    } catch (err) {
      enqueueSnackbar(`Failed to load equipment models: ${err}`, { variant: 'error' });
    } finally {
      setDefsLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (open) {
      setFormData(mapFullToUnit(equipmentProp));
      loadDefs();
    }
  }, [open, equipmentProp]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Field update helper ───────────────────────────────────────────────────

  const update = useCallback(<K extends keyof EquipmentUnit>(field: K, value: EquipmentUnit[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  // ── Render ────────────────────────────────────────────────────────────────

  const hasErrors = errorMessages && Object.keys(errorMessages).length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ backgroundColor: theme.palette.background.default, color: theme.palette.text.primary }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalShipping fontSize="small" />
            {isEditMode ? 'Edit Equipment' : 'Add New Equipment'}
          </Typography>
          <IconButton onClick={onClose} sx={{ color: theme.palette.text.primary }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: theme.palette.background.paper }}>
        {hasErrors && (
          <Box sx={{ backgroundColor: '#fee2e2', border: '1px solid #ef4444', color: '#b91c1c', p: 2, borderRadius: 1, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Please fix the following errors:
            </Typography>
            {Object.values(errorMessages!).map((err, idx) => (
              <Typography key={idx} variant="body2">• {err}</Typography>
            ))}
          </Box>
        )}

        <Paper elevation={3} sx={{ p: 2, backgroundColor: theme.palette.background.paper, borderRadius: 2 }}>
          <Grid container spacing={3}>

            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField fullWidth required label="Unit Number" value={formData.code}
                    onChange={(e) => update('code', e.target.value)} />
                </Grid>
                <Grid item xs={4}>
                  <TextField fullWidth required label="Unit Name" value={formData.displayName}
                    onChange={(e) => update('displayName', e.target.value)} />
                </Grid>
                <Grid item xs={4}>
                  {defsLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '56px' }}>
                      <CircularProgress size={24} sx={{ color: '#667eea' }} />
                    </Box>
                  ) : (
                    <TextField select fullWidth label="Model" value={formData.definitionId}
                      onChange={(e) => {
                        const defId = Number(e.target.value);
                        const def = equipmentDefs.find((d) => d.id === defId);
                        update('definitionId', defId);
                        update('equipmentModel', def?.equipmentModel ?? '');
                      }}>
                      {equipmentDefs.map((def) => (
                        <MenuItem key={def.id} value={def.id}>{def.equipmentModel}</MenuItem>
                      ))}
                    </TextField>
                  )}
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField fullWidth label="Deployment Year" type="number"
                    value={formData.deploymentYear} inputProps={{ min: 1 }}
                    onChange={(e) => update('deploymentYear', Number(e.target.value))} />
                </Grid>
                <Grid item xs={4}>
                  <TextField fullWidth label="Manufacture Year" type="number"
                    value={formData.manufactureYear} inputProps={{ min: 1 }}
                    onChange={(e) => update('manufactureYear', Number(e.target.value))} />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox checked={formData.isActive}
                    onChange={(e) => update('isActive', e.target.checked)} />
                }
                label="Active Equipment"
              />
            </Grid>

          </Grid>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ backgroundColor: theme.palette.background.default }}>
        <Button variant="contained" sx={gradientButtonSx} onClick={() => onSubmit(formData)}>
          {isEditMode ? 'Update Equipment' : 'Create Equipment'}
        </Button>
        <Button variant="outlined" onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddNewEquipmentPopUp;